import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Map, Pickaxe, Hammer, Zap, ChevronRight, AlertTriangle, Check, X } from 'lucide-react'
import { useCurrentAction } from '../hooks/useCurrentAction'
import { BoosterModal } from './BoosterModal'
import { useToast } from '../contexts/ToastContext'
import { openStationModal } from '../utils/openStationModal'

const ActionHUD = () => {
  const navigate = useNavigate()
  const { currentAction, refetch } = useCurrentAction()
  const toast = useToast()

  // State for hover and auto-collapse
  const [isHovered, setIsHovered] = useState(false)
  const [isAutoExpanded, setIsAutoExpanded] = useState(true)

  const [timeRemaining, setTimeRemaining] = useState(0)
  const [progress, setProgress] = useState(0)

  // Booster modal state
  const [showBoosterModal, setShowBoosterModal] = useState(false)

  // Fires once per action when the countdown crosses zero without the action
  // being marked completed — a missed WebSocket completion event. Re-fetching
  // /player/state restores server truth (Complete/Failed/cleared) without a
  // manual page refresh.
  const zeroCrossRef = useRef(false)

  useEffect(() => {
    zeroCrossRef.current = false
  }, [currentAction?.type, currentAction?.start_time, currentAction?.end_time])

  // Auto-collapse effect
  useEffect(() => {
    // When a new action starts, expand.
    setIsAutoExpanded(true)

    // Auto-collapse after 3s
    const timer = setTimeout(() => {
      setIsAutoExpanded(false)
    }, 3000)

    return () => clearTimeout(timer)
  }, [currentAction?.type, currentAction?.start_time])

  // Update timer every second
  useEffect(() => {
    if (!currentAction) return

    const updateTimer = () => {
      // If server says completed, force 100%
      if (currentAction.completed) {
        setTimeRemaining(0)
        setProgress(100)
        return
      }
      // Parse dates safely
      const endTimeStr = currentAction.end_time.endsWith('Z') || currentAction.end_time.includes('+')
        ? currentAction.end_time
        : currentAction.end_time + 'Z'
      const startTimeStr = currentAction.start_time.endsWith('Z') || currentAction.start_time.includes('+')
        ? currentAction.start_time
        : currentAction.start_time + 'Z'

      const now = Date.now()
      const endTimeMs = new Date(endTimeStr).getTime()
      const startTimeMs = new Date(startTimeStr).getTime()

      const remaining = Math.max(0, (endTimeMs - now) / 1000)
      const duration = (endTimeMs - startTimeMs) / 1000
      const elapsed = Math.max(0, (now - startTimeMs) / 1000)

      // Calculate progress
      const progressPercent = duration > 0 ? Math.min(100, (elapsed / duration) * 100) : 0

      setTimeRemaining(remaining)
      setProgress(progressPercent)

      // Countdown expired but no completion event was received (e.g. WebSocket
      // was down) — pull server truth once so the HUD flips to Complete/Failed
      // or clears instead of showing "In Progress 0:00" forever.
      if (remaining <= 0 && !zeroCrossRef.current) {
        zeroCrossRef.current = true
        refetch()
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [currentAction, refetch])

  const location = useLocation()
  const isScavengingView = location.pathname.startsWith('/scavenging')
  // Workbench pages already show the full gathering experience — hide the HUD there
  const isGatheringWorkbench = /^\/gathering\/\d+$/.test(location.pathname)
  // Rest/sleep actions are handled by the Shelter Amenities panel — hide HUD there
  const isRestSleep = ['resting', 'sleeping'].includes(currentAction?.type)
  const isRestSleepView = location.pathname.startsWith('/shelter')
  // A completed rest/sleep has nothing to collect or view — the Amenities panel
  // shows the outcome. Never render the HUD for one (otherwise a finished rest
  // lingers as "Complete 0:00" on every page except /shelter until the server
  // lock clears).
  if (!currentAction || (currentAction.type === 'scavenging' && isScavengingView) || (currentAction.type === 'gathering' && isGatheringWorkbench) || (isRestSleep && isRestSleepView) || (isRestSleep && currentAction.completed)) return null
  const minutes = Math.floor(timeRemaining / 60)
  const seconds = Math.floor(timeRemaining % 60)

  // Gatherings are toggles - determine if we should show timer
  const isGathering = currentAction.type === 'gathering'
  const isCrafting = currentAction.type === 'crafting'
  const showTimer = !isGathering

  // Theme Configuration — dark HUD surface with a single muted accent per
  // action type (matches the game's desaturated token palette; accent is used
  // for icon/progress/border tint, never as a full background fill).
  const actionThemes = {
    scavenging: {
      icon: Map,
      label: 'Scavenging',
      accent: 'var(--color-accent-green)',
      navPath: '/scavenging'
    },
    gathering: {
      icon: Pickaxe,
      label: 'Gathering',
      accent: 'var(--color-accent-orange)',
      navPath: '/gathering'
    },
    crafting: {
      icon: Hammer,
      label: 'Crafting',
      accent: 'var(--color-accent-orange-dark)',
      navPath: '/crafting'
    }
  }

  const theme = actionThemes[currentAction.type] || actionThemes.scavenging

  // Status Logic
  let statusText = 'In Progress'
  let timerText = `${minutes}:${seconds.toString().padStart(2, '0')}`
  let isActionComplete = false
  let isFailed = false

  if (currentAction.completed) {
    isActionComplete = true
    if (currentAction.died) {
      statusText = 'Failed'
      timerText = '0:00'
      isFailed = true
    } else {
      statusText = 'Complete'
      timerText = '0:00'
    }
  }

  const accent = isFailed ? 'var(--color-accent-red)' : theme.accent
  const Icon = theme.icon
  const StatusIcon = isFailed ? X : isActionComplete ? Check : null

  // Determine visibility state
  const isExpanded = isHovered || isAutoExpanded || (isActionComplete && !isHovered)

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none px-4">
      <div
        className="pointer-events-auto flex flex-col items-center w-full max-w-sm transition-transform duration-500"
        style={{
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          // When collapsed, translate up but leave the bottom ~14px visible
          transform: isExpanded ? 'translateY(0)' : 'translateY(calc(-100% + 14px))',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Stamina Warning (Crafting only) */}
        {isCrafting && isExpanded && (
          <div
            className="bg-bg-secondary/95 backdrop-blur-sm border border-border-secondary border-b-0 text-text-tertiary px-3 py-1 text-[11px] font-medium rounded-t-md z-0 flex items-center gap-1.5"
          >
            <AlertTriangle size={11} className="text-accent-yellow" />
            No Stamina Recovery
          </div>
        )}

        {/* Main Card — frosted dark surface, thin accent hairline on top */}
        <div
          className="relative w-full rounded-b-lg rounded-t-sm border border-border-secondary border-t-0 backdrop-blur-md transition-shadow duration-300 z-10 overflow-hidden"
          style={{
            backgroundColor: 'rgba(26, 26, 26, 0.92)',
            boxShadow: isActionComplete
              ? `0 2px 12px rgba(0,0,0,0.5), 0 0 14px ${accent}22`
              : '0 2px 10px rgba(0,0,0,0.45)',
          }}
        >
          {/* Accent hairline (top edge) */}
          <div
            className="h-[2px] w-full"
            style={{ backgroundColor: accent, opacity: isActionComplete ? 1 : 0.8 }}
          />

          {/* Card Content */}
          <div className="flex flex-col px-3 pt-2.5 pb-1.5 gap-2">

            {/* Top Row: Icon/Name --- Status/Timer --- Button */}
            <div className="flex items-center justify-between gap-3">

              {/* Left: Icon & Label */}
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                <span
                  className="flex items-center justify-center h-7 w-7 rounded-md flex-shrink-0"
                  style={{ backgroundColor: `${accent}1f`, border: `1px solid ${accent}40` }}
                >
                  <Icon size={14} style={{ color: accent }} />
                </span>
                <div className="flex flex-col leading-tight min-w-0">
                  <span className="text-xs font-semibold text-text-primary truncate">
                    {currentAction.label || theme.label}
                  </span>
                  <span className="text-[10px] text-text-tertiary font-mono uppercase tracking-widest">
                    {currentAction.type}
                  </span>
                </div>
              </div>

              {/* Right: Status & Timer & Action Button */}
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                {/* Timer/Status Block */}
                <div className="flex flex-col items-end min-w-[72px]">
                  {showTimer ? (
                    <>
                      <span
                        className="text-[10px] font-medium uppercase tracking-widest mb-0.5 flex items-center gap-1"
                        style={{ color: isActionComplete ? accent : 'var(--color-text-muted)' }}
                      >
                        {StatusIcon && <StatusIcon size={10} />}
                        {statusText}
                      </span>
                      <span
                        className="font-mono text-sm tabular-nums"
                        style={{ color: isActionComplete ? accent : 'var(--color-text-primary)' }}
                      >
                        {timerText}
                      </span>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="relative flex h-1.5 w-1.5">
                        <span
                          className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                          style={{ backgroundColor: accent }}
                        ></span>
                        <span
                          className="relative inline-flex rounded-full h-1.5 w-1.5"
                          style={{ backgroundColor: accent }}
                        ></span>
                      </span>
                      <span className="text-[11px] font-medium text-text-primary">Active</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1.5">
                  {/* Boost Button - only show when action is in progress and not rest/sleep */}
                  {!isActionComplete && !isRestSleep && (
                    <button
                      onClick={() => setShowBoosterModal(true)}
                      className="h-7 px-2.5 rounded-md text-[11px] font-medium uppercase tracking-wide transition-all active:scale-95 hover:brightness-125 flex items-center gap-1 text-accent-yellow border border-accent-yellow/25 bg-accent-yellow/10"
                    >
                      <Zap size={11} />
                      Boost
                    </button>
                  )}

                  {/* Go Button */}
                  <button
                    onClick={() => {
                      // Audit L2: on /crafting the player is already "there" —
                      // same-path navigate() is a no-op, so open the station's
                      // recipe modal (or glow its card) instead. Other pages
                      // keep the navigate fallback.
                      if (isCrafting && openStationModal(currentAction.label)) return
                      // Rest/sleep actions navigate to shelter, not scavenging
                      if (isRestSleep) return navigate('/shelter')
                      navigate(theme.navPath)
                    }}
                    className="h-7 px-2.5 rounded-md text-[11px] font-medium uppercase tracking-wide transition-all active:scale-95 flex items-center gap-0.5 text-text-secondary bg-white/5 border border-white/10 hover:bg-white/10"
                  >
                    View
                    <ChevronRight size={11} className="opacity-60" />
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom Row: Progress Bar (Only if showing timer) */}
            {showTimer && (
              <div className="h-1 w-full bg-black/40 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-300 ease-linear rounded-full"
                  style={{
                    width: `${progress}%`,
                    backgroundColor: accent,
                    opacity: isActionComplete ? 1 : 0.9
                  }}
                />
              </div>
            )}

          </div>

          {/* Handle Area (Always at bottom) */}
          <div className="h-2 w-full flex items-center justify-center pb-0.5">
            <div className="w-10 h-[3px] rounded-full bg-white/20" />
          </div>

        </div>
      </div>

      {/* Booster Modal */}
      <BoosterModal
        isOpen={showBoosterModal}
        onClose={() => setShowBoosterModal(false)}
        actionType={currentAction?.type}
        runId={currentAction?.run_id || currentAction?.job_id}
        currentRemaining={Math.floor(timeRemaining)}
        onBoostSuccess={(result) => {
          const message = result.time_reduced_seconds
            ? `Boosted with ${result.booster_used}! Reduced time by ${Math.floor(result.time_reduced_seconds / 60)}m ${result.time_reduced_seconds % 60}s`
            : result.ticks_processed
              ? `Boosted with ${result.booster_used}! Processed ${result.ticks_processed} ticks`
              : `Boosted with ${result.booster_used}!`
          toast.success(message)
          // Refresh action state to update timer
          if (refetch) {
            refetch()
          }
        }}
      />
    </div>
  )
}

export default ActionHUD
